import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Input, Select, Button, List, Tag, Segmented, message, Empty,
} from 'antd';
import {
  PlusOutlined, PaperClipOutlined, CloseCircleFilled, HistoryOutlined, RightOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getWorkTasks, getAssignableUsers, createWorkTask } from '../api/workTasks';
import { getBranches } from '../api/branches';
import useAuthStore from '../store/authStore';
import useDebounce from '../hooks/useDebounce';
import usePersistedPageSize from '../hooks/usePersistedPageSize';
import { URGENCY_OPTIONS, urgencyMeta } from '../constants/agendaUrgency';
import { buildTimeline, formatBytes, ATTACHMENT_ACCEPT, workTaskDetailPath } from '../components/workTasks/workTaskHelpers';
import { linkifyText } from '../utils/linkify';

const { TextArea } = Input;
const { Option } = Select;
const MANAGER_ROLES = ['branch_manager', 'super_admin', 'developer_admin'];
const TOP_ROLES = ['super_admin', 'developer_admin'];

/**
 * İş Takip listesi.
 *
 * Bu sayfa yalnızca LİSTELEME ve YENİ İŞ AÇMA yapar. Not ekleme, devretme,
 * tamamlama, dosya işlemleri ve hareket geçmişi işin kendi detay sayfasında
 * (/work-tasks/:id). Önceden hepsi burada satır içi modallardaydı; bildirimden
 * ("X kişisi şu işe not ekledi") tekil bir işe gidilemediği ve hareket geçmişi
 * satır içinde okunaksız kaldığı için ayrıldı.
 */
const WorkTasks = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isManager = MANAGER_ROLES.includes(user?.role);
  const isTopRole = TOP_ROLES.includes(user?.role);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [description, setDescription] = useState('');
  const [newUrgency, setNewUrgency] = useState('none');
  const [assigneeIds, setAssigneeIds] = useState([]);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [assigneeLoading, setAssigneeLoading] = useState(false);
  const assigneeSearchTimer = useRef(null);
  const [newFiles, setNewFiles] = useState([]);
  const newFilesInputRef = useRef(null);

  const [tab, setTab] = useState('to_me');
  const [urgencyFilter, setUrgencyFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [sortBy, setSortBy] = useState('created_at');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);

  // "Şube Geneli" görünümü için: yöneticiler şube bazında filtreleyebiliyor
  // (developer_admin/super_admin tüm şubeleri görür, aralarında seçim
  // yapabilir; branch_manager zaten backend'de kendi şubesine kısıtlı).
  // Kişiye göre filtreleme de aynı görünümde - "bu işler kime atanmış" sorusuna
  // arama kutusundan cevap vermek için.
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState(null);
  const [personFilter, setPersonFilter] = useState(null);
  const [personOptions, setPersonOptions] = useState([]);
  const [personLoading, setPersonLoading] = useState(false);
  const personSearchTimer = useRef(null);

  const [persistedPageSize, setPersistedPageSize] = usePersistedPageSize('work-tasks', 15);
  const [pagination, setPagination] = useState({ current: 1, pageSize: persistedPageSize, total: 0 });

  useEffect(() => {
    if (isTopRole) {
      getBranches({ per_page: 100 }).then(res => setBranches(res.data.data || [])).catch(() => {});
    }
  }, [isTopRole]);

  const handlePersonSearch = (text) => {
    clearTimeout(personSearchTimer.current);
    if (!text || text.trim().length < 2) { setPersonOptions([]); return; }
    personSearchTimer.current = setTimeout(async () => {
      setPersonLoading(true);
      try {
        const res = await getAssignableUsers({ search: text.trim() });
        setPersonOptions(res.data || []);
      } catch {
        setPersonOptions([]);
      } finally {
        setPersonLoading(false);
      }
    }, 300);
  };

  const fetchItems = useCallback(async (page = 1, perPage = pagination.pageSize) => {
    setLoading(true);
    try {
      const res = await getWorkTasks({
        filter: tab,
        page,
        per_page: perPage,
        search: debouncedSearch || undefined,
        urgency: urgencyFilter || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        sort_by: sortBy,
        branch_id: tab === 'all' ? (branchFilter || undefined) : undefined,
        assigned_to_user_id: tab === 'all' ? (personFilter || undefined) : undefined,
      });
      setItems(res.data.data);
      setPagination({
        current: res.data.current_page,
        pageSize: res.data.per_page,
        total: res.data.total,
      });
    } catch (error) {
      message.error('İşler yüklenemedi.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, debouncedSearch, urgencyFilter, statusFilter, sortBy, branchFilter, personFilter]);

  useEffect(() => {
    fetchItems(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, debouncedSearch, urgencyFilter, statusFilter, sortBy, branchFilter, personFilter]);

  const handleAssigneeSearch = (text) => {
    clearTimeout(assigneeSearchTimer.current);
    if (!text || text.trim().length < 2) { setAssigneeOptions([]); return; }
    assigneeSearchTimer.current = setTimeout(async () => {
      setAssigneeLoading(true);
      try {
        const res = await getAssignableUsers({ search: text.trim() });
        setAssigneeOptions(res.data || []);
      } catch {
        setAssigneeOptions([]);
      } finally {
        setAssigneeLoading(false);
      }
    }, 300);
  };

  const handleCreate = async () => {
    if (!description.trim()) {
      message.warning('İş açıklaması boş olamaz.');
      return;
    }
    if (assigneeIds.length === 0) {
      message.warning('Kime atanacağını seçin.');
      return;
    }
    setSubmitting(true);
    try {
      await createWorkTask({
        description: description.trim(),
        urgency: newUrgency,
        assigned_to_user_ids: assigneeIds,
      }, newFiles);
      setDescription('');
      setNewUrgency('none');
      setAssigneeIds([]);
      setAssigneeOptions([]);
      setNewFiles([]);
      message.success('İş atandı.');
      fetchItems(1, pagination.pageSize);
    } catch (error) {
      message.error(error?.response?.data?.message || 'İş oluşturulamadı.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewFilesChange = (e) => {
    const picked = Array.from(e.target.files || []);
    if (newFilesInputRef.current) newFilesInputRef.current.value = '';
    if (picked.length === 0) return;
    setNewFiles((prev) => [...prev, ...picked].slice(0, 10));
  };

  const handleRemoveNewFile = (index) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const tabOptions = [
    { label: 'Bana Atananlar', value: 'to_me' },
    { label: 'Benim Atadıklarım', value: 'by_me' },
    ...(isManager ? [{ label: 'Şube Geneli', value: 'all' }] : []),
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">İş Takip</h2>

      <Card className="mb-4">
        <TextArea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Yapılacak işi yazın..."
          autoSize={{ minRows: 2, maxRows: 6 }}
          maxLength={5000}
        />
        <div className="flex flex-wrap justify-between items-center gap-2 mt-3">
          <div className="flex flex-wrap gap-2">
            <Select
              mode="multiple"
              showSearch
              value={assigneeIds}
              onChange={setAssigneeIds}
              onSearch={handleAssigneeSearch}
              filterOption={false}
              loading={assigneeLoading}
              placeholder="Kime atanacak? (en az 2 karakter yazın, birden fazla seçilebilir)"
              style={{ width: 320 }}
              notFoundContent={assigneeLoading ? 'Aranıyor...' : 'Kullanıcı bulunamadı'}
            >
              {assigneeOptions.map((u) => (
                <Option key={u.id} value={u.id}>
                  {u.name} {u.surname}{u.branch?.name ? ` (${u.branch.name})` : ''}
                </Option>
              ))}
            </Select>
            <Select value={newUrgency} onChange={setNewUrgency} style={{ width: 180 }}>
              {URGENCY_OPTIONS.map((o) => (
                <Option key={o.value} value={o.value}>{o.label}</Option>
              ))}
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Button icon={<PaperClipOutlined />} onClick={() => newFilesInputRef.current?.click()}>
              Dosya Ekle
            </Button>
            <Button type="primary" icon={<PlusOutlined />} loading={submitting} onClick={handleCreate}>
              İşi Ata
            </Button>
          </div>
        </div>
        {newFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {newFiles.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center gap-1 px-2 py-1 rounded border text-xs"
                style={{ background: '#fafafa', borderColor: '#e5e5e5' }}
              >
                <PaperClipOutlined className="text-gray-500" />
                <span>{file.name}</span>
                <span className="text-gray-400">{formatBytes(file.size)}</span>
                <CloseCircleFilled
                  className="text-gray-400 hover:text-red-500 cursor-pointer"
                  onClick={() => handleRemoveNewFile(index)}
                />
              </div>
            ))}
          </div>
        )}
        <input
          ref={newFilesInputRef}
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          style={{ display: 'none' }}
          onChange={handleNewFilesChange}
        />
      </Card>

      <Card>
        <div className="flex flex-wrap gap-3 mb-4 items-center justify-between">
          <Segmented value={tab} onChange={setTab} options={tabOptions} />
          <div className="flex flex-wrap gap-3 items-center">
            <Input
              placeholder="Açıklamada ara..."
              allowClear
              style={{ width: 220 }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {tab === 'all' && (
              <Select
                showSearch
                allowClear
                placeholder="Kişiye göre filtrele (en az 2 karakter)"
                style={{ width: 240 }}
                value={personFilter}
                onChange={(v) => setPersonFilter(v || null)}
                onSearch={handlePersonSearch}
                filterOption={false}
                loading={personLoading}
                notFoundContent={personLoading ? 'Aranıyor...' : 'Kullanıcı bulunamadı'}
              >
                {personOptions.map((u) => (
                  <Option key={u.id} value={u.id}>
                    {u.name} {u.surname}{u.branch?.name ? ` (${u.branch.name})` : ''}
                  </Option>
                ))}
              </Select>
            )}
            {tab === 'all' && isTopRole && (
              <Select
                allowClear
                placeholder="Şube filtrele"
                style={{ width: 180 }}
                value={branchFilter}
                onChange={(v) => setBranchFilter(v || null)}
              >
                {branches.map((b) => (
                  <Option key={b.id} value={b.id}>{b.name}</Option>
                ))}
              </Select>
            )}
            <Select
              placeholder="Aciliyete göre filtrele"
              allowClear
              style={{ width: 200 }}
              value={urgencyFilter}
              onChange={(v) => setUrgencyFilter(v || null)}
            >
              {URGENCY_OPTIONS.map((o) => (
                <Option key={o.value} value={o.value}>{o.label}</Option>
              ))}
            </Select>
            <Segmented
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { label: 'Tümü', value: 'all' },
                { label: 'Bekleyen', value: 'pending' },
                { label: 'Tamamlanan', value: 'completed' },
              ]}
            />
            <Select
              placeholder="Sırala"
              style={{ width: 180 }}
              value={sortBy}
              onChange={(v) => setSortBy(v)}
            >
              <Option value="created_at">En Yeni</Option>
              <Option value="urgency">Aciliyete Göre</Option>
            </Select>
          </div>
        </div>

        <List
          loading={loading}
          dataSource={items}
          locale={{ emptyText: <Empty description="İş bulunamadı." /> }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['15', '30', '50', '100'],
            onChange: (page, pageSize) => {
              setPersistedPageSize(pageSize);
              fetchItems(page, pageSize);
            },
            onShowSizeChange: (page, pageSize) => {
              setPersistedPageSize(pageSize);
              fetchItems(1, pageSize);
            },
            showTotal: (total) => `Toplam ${total} iş`,
          }}
          renderItem={(item) => {
            const meta = urgencyMeta(item.urgency);
            const assignees = item.assignees || [];
            const isFullyCompleted = assignees.length > 0 && assignees.every((a) => a.is_completed);
            const movementCount = buildTimeline(item).length;

            return (
              <List.Item
                key={item.id}
                onClick={() => navigate(workTaskDetailPath(user?.role, item.id))}
                style={{ cursor: 'pointer' }}
                className="hover:bg-gray-50"
                extra={<RightOutlined className="text-gray-300" />}
              >
                <List.Item.Meta
                  title={
                    <div className="flex items-center gap-2 flex-wrap">
                      <Tag color={meta.color}>{meta.label}</Tag>
                      {isFullyCompleted && <Tag color="green">Tamamlandı</Tag>}
                      <span className="text-gray-700 text-sm font-normal">
                        {item.assigned_by?.name} {item.assigned_by?.surname} →{' '}
                        {assignees.map((a, i) => (
                          <span key={a.id}>
                            {i > 0 && ', '}
                            <span style={{ color: a.is_completed ? '#52c41a' : 'inherit' }}>
                              {a.is_completed && '✓ '}{a.user?.name} {a.user?.surname}
                            </span>
                          </span>
                        ))}
                      </span>
                      {movementCount > 1 && (
                        <Tag icon={<HistoryOutlined />}>{movementCount} hareket</Tag>
                      )}
                      <span className="text-gray-400 text-xs">
                        {dayjs(item.created_at).format('DD.MM.YYYY HH:mm')}
                      </span>
                    </div>
                  }
                  description={
                    <div>
                      <div
                        style={{
                          whiteSpace: 'pre-wrap',
                          textDecoration: isFullyCompleted ? 'line-through' : 'none',
                          color: isFullyCompleted ? '#999' : 'inherit',
                        }}
                      >
                        {linkifyText(item.description)}
                      </div>
                      {/* Tamamlama notları listede kalıyor: bunlar işlem değil
                          bilgi, "bitti mi, nasıl bitti" sorusunun cevabını
                          detaya girmeden görmek işi hızlandırıyor. */}
                      {assignees.filter((a) => a.is_completed && a.completion_note).map((a) => (
                        <div
                          key={a.id}
                          style={{
                            whiteSpace: 'pre-wrap',
                            marginTop: 4,
                            padding: '4px 8px',
                            background: '#f6ffed',
                            border: '1px solid #d9f7be',
                            borderRadius: 4,
                            color: '#389e0d',
                            fontSize: 13,
                          }}
                        >
                          {a.user?.name} notu: {a.completion_note}
                        </div>
                      ))}
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      </Card>
    </div>
  );
};

export default WorkTasks;
